import React, { useEffect } from 'react'
import { PropTypes } from 'prop-types'
import { useIntl } from 'react-intl'
import { useHistory } from 'react-router-dom'
import { useRecoilCallback, useRecoilValue } from 'recoil'

import {
  DataTable,
  Link,
  Spinner,
  HelpIcon,
  SkeletonLoader,
  SKELETON_LOADER_VARIANT_ONWHITE,
  Flex,
  FlexItem,
  ICON_FA_VARIANT_SOLID,
  ButtonAction,
  ACTION_BUTTON_TYPE_EDIT,
  TooltipUtils,
  Panel
} from 'mailup-web-components'

import {
  linkcheckSummaryBoxState,
  messageLink,
  messageState
} from 'Pages/MessageCheckupLinkCheck/recoil/state/message'
import { canSendState } from 'Pages/MessageCheckupLinkCheck/recoil/state/page'
import {
  checkLinkCallback,
  editCallback
} from 'Pages/MessageCheckupLinkCheck/recoil/callbacks/callbacks'
import { IconKO, IconOK } from '../../commonComponents/commonComponents'
import styled from '@emotion/styled'

const TableLinks = ({ linkType }) => {
  const intl = useIntl()
  const history = useHistory()

  const {
    links,
    customParams,
    showJustUnreachable,
    showJustBadReputation
  } = useRecoilValue(messageState)
  const canSend = useRecoilValue(canSendState)
  const { areAllLinksReachable } = useRecoilValue(linkcheckSummaryBoxState)

  const checkLink = useRecoilCallback(checkLinkCallback, [])
  const edit = useRecoilCallback(editCallback, [])

  const rowData = links[linkType]

  const atLeastOneNotValid = !areAllLinksReachable() && canSend // if atLeastOneNotValid shows column of Edit

  const getHeads = () => {
    const heads = [
      {
        span:
          linkType === 'image' && !atLeastOneNotValid
            ? 10
            : (linkType === 'image' && atLeastOneNotValid) ||
              !atLeastOneNotValid
            ? 8
            : 6,
        content: intl.formatMessage({
          id: 'Load.ActiveRecipientClickDetails.Url'
        })
      }
    ]

    if (linkType === 'link') {
      heads.push({
        span: 2,
        content: intl.formatMessage({
          id: 'Load.EmailCheckUp.LinkCheckLinkReputationHeaderNew'
        }),
        align: 'left'
      })
    }

    heads.push({
      span: 2,
      content: intl.formatMessage({
        id: 'Load.EmailCheckUp.LinkCheckLinkValidationHeaderNew'
      }),
      align: 'left'
    })

    if (atLeastOneNotValid) {
      heads.push({ span: 2 })
    }

    return heads
  }

  const getBodyRow = item => {
    const rows = []
    const {
      link,
      loading,
      loaded,
      reachability,
      action,
      issues
    } = useRecoilValue(messageLink(item.Url))

    let concatParamsChar = ''
    try {
      concatParamsChar = new URL(item.Url).search === '' ? '?' : '&'
    } catch (e) {
      concatParamsChar = ''
    }

    useEffect(() => {
      checkLink(linkType, item.Url)
    }, [])

    if (
      (showJustUnreachable && reachability) ||
      (showJustBadReputation && action === 0)
    ) {
      return undefined
    }

    const getErrorMessage = () => {
      let formattedMessage = intl.formatMessage(
        {
          id: 'Load.EmailCheckUp.ValidationLinkErrorNew'
        },
        {
          strong: x => <strong>{x}</strong>
        }
      )

      if (issues.length > 0) {
        if (linkType === 'link') {
          let label = `Load.AsyncDetail.Blacklist_${issues[0].Code}`

          if (issues[0].Code !== 'BAD_FORMAT' && issues[0].Code !== 'IP') {
            const blacklistCase =
              issues[0].Code.substr(0, 6) === 'GOOGLE'
                ? 'BlackList'
                : 'Blacklist'
            label = `Load.AsyncDetail.${blacklistCase}_${issues[0].Code}New`
            formattedMessage = intl.formatMessage(
              { id: 'Load.AsyncDetail.BlacklistInfo' },
              {
                strong: x => <strong>{x}</strong>,
                message: intl.formatMessage({ id: label })
              }
            )
          } else {
            formattedMessage = intl.formatMessage({
              id: label
            })
          }
        }
      }
      return formattedMessage
    }

    let renderLinkPart1 = ''
    try {
      renderLinkPart1 =
        linkType === 'link' && customParams !== ''
          ? new URL(item.Url).href + concatParamsChar
          : link.Url
    } catch (e) {
      renderLinkPart1 = 'url-not-valid'
    }

    const renderLinkPart2 =
      linkType === 'link' ? customParams.replace(/\?/g, '') : ''

    const handleEditClick = () => {
      edit({
        onRedirect: newRoute => {
          history.push(newRoute)
        }
      })
    }

    const urlContent =
      loading && !(renderLinkPart1 || renderLinkPart2) ? (
        <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} width='80%' />
      ) : (
        <>
          <StyledLink
            href={renderLinkPart1 + renderLinkPart2}
            target={'_blank'}
            rel='noopener noreferrer'
          >
            <span className='left'>{renderLinkPart1}</span>
            <span className='right'>{renderLinkPart2}</span>
          </StyledLink>
        </>
      )

    rows.push({ content: urlContent })

    if (linkType === 'link') {
      const reputationContent = (
        <Flex alignItems='flex-end' justifyContent='center'>
          {loading ? (
            <>
              <Spinner />
              {atLeastOneNotValid && (
                <FlexItem width='15px' margin='0 0 0 4px' />
              )}
            </>
          ) : (
            <>
              {action === 0 ? (
                <IconOK variant={ICON_FA_VARIANT_SOLID} />
              ) : (
                <IconKO variant={ICON_FA_VARIANT_SOLID} />
              )}
              {action > 0 && issues.length > 0 ? (
                <>
                  <TooltipUtils
                    id='errorToolip'
                    type='light'
                    className='c-tooltip'
                  >
                    {getErrorMessage()}
                  </TooltipUtils>
                  <span data-tip data-for='errorToolip'>
                    <HelpIcon />
                  </span>
                </>
              ) : (
                atLeastOneNotValid && (
                  <FlexItem width='15px' margin='0 0 0 4px' />
                )
              )}
            </>
          )}
        </Flex>
      )

      rows.push({ content: reputationContent })
    }

    const reachabilityContent = (
      <Flex columnGap='4px' alignItems='flex-end' justifyContent='center'>
        {loading ? (
          <>
            <Spinner />
            {atLeastOneNotValid && <FlexItem width='15px' margin='0 0 0 4px' />}
          </>
        ) : (
          <>
            {reachability ? (
              <>
                <IconOK variant={ICON_FA_VARIANT_SOLID} />
                {atLeastOneNotValid && (
                  <FlexItem width='15px' margin='0 0 0 4px' />
                )}
              </>
            ) : (
              <>
                <IconKO variant={ICON_FA_VARIANT_SOLID} />
                <>
                  <TooltipUtils
                    id='errorToolip'
                    type='light'
                    effect='solid'
                    className='c-tooltip'
                  >
                    {getErrorMessage()}
                  </TooltipUtils>
                  <span data-tip data-for='errorToolip'>
                    <HelpIcon />
                  </span>
                </>
              </>
            )}
          </>
        )}
      </Flex>
    )
    rows.push({ content: reachabilityContent })

    if (atLeastOneNotValid) {
      let actionsContent = <></>
      if (canSend && !areAllLinksReachable() && !reachability) {
        actionsContent = (
          <Flex columnGap='8px' alignItems='baseline' justifyContent='flex-end'>
            {loaded && (
              <>
                <ButtonAction
                  type={ACTION_BUTTON_TYPE_EDIT}
                  onClick={handleEditClick}
                />
              </>
            )}
          </Flex>
        )
      }
      rows.push({ content: actionsContent })
    }

    return rows
  }

  return (
    <Panel>
      <DataTable
        rows={
          rowData.map(item => getBodyRow(item)).filter(elem => !!elem).length
        }
        head={getHeads()}
        sortable={false}
        body={rowData.map(item => getBodyRow(item)).filter(elem => !!elem)}
      />
    </Panel>
  )
}

TableLinks.propTypes = {
  linkType: PropTypes.string
}

export default TableLinks

const StyledLink = styled(Link)`
  .left {
    color: var(--boulder);
  }
  .right {
    color: var(--silver-chalice);
  }
  :hover{
      text-decoration underline;
      color: var(--boulder);
  }
`

const DatatableContainer = styled.span`
  table.c-datatable--head
    .c-datatable__cell--2
    > .c-datatable__cell-title-wrapper {
    justify-content: center;
  }
`
